import { getErrorMessage } from "@/lib/getErrorMessage";

/**
 * Double authentification (TOTP) — la partie PURE côté navigateur.
 *
 * Elle n'est exigée que du mode support (migration 20261002120000) : la base y
 * refuse tout droit à un jeton `aal1`, et `SupportMfaGate` affiche l'écran du code
 * avant de monter la moindre page. Franchisés et salariés ne sont pas concernés.
 *
 * Supabase Auth grave le niveau atteint dans le JWT : `aal1` après le mot de passe,
 * `aal2` après le code. Ce module le lit SANS le SDK, parce que l'endroit qui doit
 * le connaître en premier est le callback `onAuthStateChange` de `StoreProvider`,
 * où un appel au SDK se bloque (piège relevé sur Creerbail le 03/09/2026).
 * `getAuthenticatorAssuranceLevel()` fait le même calcul, de façon asynchrone.
 */

export type AssuranceLevel = "aal1" | "aal2";

/** Niveau porté par le jeton d'accès, `null` sans jeton ou s'il est illisible. */
export function assuranceLevelOfAccessToken(accessToken: string | null | undefined): AssuranceLevel | null {
  const segment = accessToken?.split(".")[1];
  if (!segment) return null;
  try {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (!payload || typeof payload !== "object") return null;
    return (payload as { aal?: unknown }).aal === "aal2" ? "aal2" : "aal1";
  } catch {
    return null;
  }
}

const TOTP_CODE_LENGTH = 6;

/** « 123 456 » → « 123456 » ; tout ce qui n'est pas six chiffres est refusé AVANT l'appel. */
export function normalizeTotpCode(input: string): string | null {
  const digits = input.replace(/\D/g, "");
  return digits.length === TOTP_CODE_LENGTH ? digits : null;
}

/** Codes levés par les RPC du support (migration 20261002120000), traduits pour l'agent. */
const SUPPORT_MFA_ERRORS: Record<string, string> = {
  mfa_required:
    "Le mode support exige la double authentification. Reconnectez-vous et saisissez le code de votre application.",
  mfa_stale: "Par sécurité, saisissez de nouveau le code de votre application pour démarrer la session.",
};

/** Message d'une erreur du mode support : la double authentification d'abord, sinon le message brut. */
export function describeSupportError(error: unknown, fallback: string): string {
  const message = getErrorMessage(error, fallback);
  const code = Object.keys(SUPPORT_MFA_ERRORS).find((key) => message.includes(key));
  return code ? SUPPORT_MFA_ERRORS[code] : message;
}
